import * as Enumerable from 'linq';
import Decimal from 'decimal.js';
import { EmbeddedCode, PendingCode, CodeStatus } from '../entities/shared/code';
import { EntityStatusType } from '../entities/entity.status/enum';
import { Job } from '../entities/job/job';
import { WorkOrder } from '../entities/job/workorder';
import { WorkOrderStatusSystemName } from '../entities/job/workorder.status.system.name';

export interface StageMoneyTotals {
    totalSell: number;
    totalBuy: number;
    claimAmount: number;
}

export interface JobMoneyTotals {
    totalSell: number;
    totalBuy: number;
}

/**
 * The single source of truth for the materialised money figures (WBS-10165). Every writer of
 * `claimAmount` / `totalSell` / `totalBuy` — the write-primitive recompute, the janitor drift-healer
 * and the backfill — must compute through here so the stored numbers can never disagree by
 * implementation.
 *
 * Canonical rules (chosen at the WBS-10089 spike; they deliberately resolve the historical
 * inconsistencies between the read-time implementations):
 *  - lines must have an _id, not be Cancelled, and not be INCENTIVE type;
 *  - line value = quantity × unit price rounded to 2dp per line, then summed (CodeUtility semantics —
 *    unit prices already carry the UoM conversion);
 *  - `claimAmount` is the contractor-supplied value on the stage: active pending claim items whose
 *    item status group is Active, plus contractor-supplied regular items (which is where approved
 *    claim lines live after moveClaimedCodesToCodes, and what markRejected zeroes);
 *  - job totals sum non-cancelled stages only.
 *
 * These figures are "value of work on the stage" — they are NOT invoice totals, which are computed
 * from the raw lines at generation time under different rules (NotBillable/already-invoiced
 * exclusions, INCENTIVE included, discounts and tax).
 */
export class StageMoneyUtility {

    public static computeStageTotals(stage: WorkOrder): StageMoneyTotals {

        const codes = StageMoneyUtility.countableCodes(stage?.codes);

        const totalSell = StageMoneyUtility.sumLines(codes, code => code.sellPrice);
        const totalBuy = StageMoneyUtility.sumLines(codes, code => code.buyPrice);

        const contractorCodes = StageMoneyUtility.contractorSuppliedCodes(stage?.codes);
        const pendingClaimCodes = (stage?.claimCodes ?? []).filter(code => !!code?._id && code.suppliedBySubcontractor === true && code.active === true && code.type !== 'INCENTIVE' && code.itemStatus?.statusType === EntityStatusType.Active);

        const claimAmount = new Decimal(StageMoneyUtility.sumLines(contractorCodes, code => code.buyPrice))
            .add(StageMoneyUtility.sumLines(pendingClaimCodes as EmbeddedCode[], code => code.buyPrice))
            .toNumber();

        return { totalSell, totalBuy, claimAmount };
    }

    public static computeJobTotals(job: Job): JobMoneyTotals {

        const stageTotals = (job?.workOrders ?? [])
            .filter(stage => stage?.status?.systemName !== WorkOrderStatusSystemName.Canceled)
            .map(stage => StageMoneyUtility.computeStageTotals(stage));

        const totalSell = stageTotals.reduce((sum, totals) => new Decimal(sum).add(totals.totalSell).toNumber(), 0);
        const totalBuy = stageTotals.reduce((sum, totals) => new Decimal(sum).add(totals.totalBuy).toNumber(), 0);

        return { totalSell, totalBuy };
    }

    /** Stamps computed totals onto the job object in place — for insert paths where the whole document is in hand and no follow-up write is wanted. */
    public static applyTotals(job: Job): void {

        if (!job) return;

        for (const stage of job.workOrders ?? []) {
            const totals = StageMoneyUtility.computeStageTotals(stage);
            stage.totalSell = totals.totalSell;
            stage.totalBuy = totals.totalBuy;
            stage.claimAmount = totals.claimAmount;
        }

        const jobTotals = StageMoneyUtility.computeJobTotals(job);
        job.totalSell = jobTotals.totalSell;
        job.totalBuy = jobTotals.totalBuy;
    }

    /**
     * Lines that count toward a money figure: real rows, not cancelled, not INCENTIVE.
     *
     * Public because the recommendation pricing engine needs the same rule when valuing a stage
     * against a candidate contractor's price book (WBS-10686) — it was previously summing everything
     * it was handed, which let cancelled lines inflate a contractor's quoted price and reorder the
     * recommendations.
     */
    public static countableCodes(codes: EmbeddedCode[] | undefined): EmbeddedCode[] {

        return (codes ?? []).filter(code => !!code?._id && code.status !== CodeStatus.Cancelled && code.type !== 'INCENTIVE');
    }

    /**
     * Countable lines that a contractor is expected to provide — the basis of what a contractor is
     * paid, and therefore of what a candidate contractor should be quoted for.
     *
     * `suppliedBySubcontractor` is a catalogue attribute carried onto the line when the code is added,
     * so it is set long before any contractor is assigned. Lines we supply ourselves are excluded:
     * pricing them against a contractor's price book is meaningless, as the contractor never charges
     * for them.
     *
     * Not Payable is excluded for the same reason it is kept off the bill: a zeroed buy price counted
     * toward `claimAmount` claims nothing while implying a line is owed.
     */
    public static contractorSuppliedCodes(codes: EmbeddedCode[] | undefined): EmbeddedCode[] {

        return StageMoneyUtility.countableCodes(codes).filter(code => code.suppliedBySubcontractor === true && code.payable !== false);
    }

    private static sumLines(codes: EmbeddedCode[], price: (code: EmbeddedCode) => number | undefined): number {

        return Enumerable
            .from(codes)
            .select(code => {

                const qty = code?.quantity ?? 1;
                const linePrice = new Decimal(qty).mul(price(code) ?? 0);
                return new Decimal(linePrice).toDP(2).toNumber();
            })
            .sum();
    }
}
